<script setup lang="ts">
import StoriesBreadcrumb from '@/components/customer-stories/StoriesBreadcrumb.vue'
import StoryCard from '@/components/customer-stories/StoryCard.vue'
import { getLatestStories, stories } from '@/data/stories'
import { getStoryBySlug, type Story } from '@/data/stories'
import { getStoryFolderByCategory } from '@/data/stories'
import { easeOut, motion } from 'motion-v'

const route = useRoute()
const isPlaying = ref(false)

const slugFromUrl = route.path.split('/').pop()
const story = getStoryBySlug(slugFromUrl?.toString() || '')

const lastThreeStories = getLatestStories(stories, slugFromUrl, 3)

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const storyVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Customer Stories',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/customer-stories`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': story?.category,
      'item': `${
        import.meta.env.VITE_MONOLITH_URL
      }/customer-stories?category=${encodeURIComponent(story?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': story?.title,
      'item': `${
        import.meta.env.VITE_MONOLITH_URL
      }/customer-stories/${getStoryFolderByCategory(story?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${
        import.meta.env.VITE_MONOLITH_URL
      }/customer-stories/${getStoryFolderByCategory(story?.category || '')}/${slugFromUrl}`,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: story?.seo?.title,
  description: story?.seo?.description,
  ogTitle: story?.seo?.title,
  ogDescription: story?.seo?.description,
  ogImage: story?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4 md:shadow-2xl md:shadow-black/10"
    >
      <section class="3xl:px-0 mx-auto mb-8 px-4 lg:max-w-screen-xl">
        <StoriesBreadcrumb :story="story as Story" />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-900 lg:text-5xl"
        >
          {{ story?.title }}
        </motion.h1>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          With an optimized website and engaging social media, Toco Grill turned inconsistent marketing into steady visibility and measurable results
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          <span v-html="story?.subtitle" />
        </motion.p>

        <div
          :variants="storyVariants"
          initial="initial"
          while-in-view="animate"
          class="relative my-6 flex flex-col justify-between gap-5 rounded-[40px] border border-gray-200 bg-white p-11 lg:flex-row xl:-ml-[3.5%] xl:w-[107%]"
        >
          <motion.p
            :variants="paragraphVariants"
            :custom="0"
            initial="initial"
            while-in-view="animate"
            class="absolute -top-1 left-11 pt-4 text-left text-slate-700"
          >
            Key Results
          </motion.p>
          <motion.div
            v-for="item in story?.highlight"
            :key="item.text"
            :variants="storyVariants"
            initial="initial"
            while-in-view="animate"
            class="flex w-full flex-col gap-y-2 rounded-2xl border border-gray-200 bg-[#f8f8f0] p-8 text-center"
          >
            <p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="text-4xl font-bold text-orange-500"
            >
              {{ item.number }}
            </p>
            <p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="text-slate-700"
            >
              {{ item.text }}
            </p>
          </motion.div>
        </div>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-4 text-left text-xl text-slate-700"
        >
          For this small kosher Mediterranean restaurant in Atlanta, SpotHopper automated marketing, improved website traffic by over 600%, and gave the owner back two hours a day—precious time to focus on guests.
        </motion.p>
        <div class="mb-5 mt-8 flex flex-col items-stretch gap-4 md:flex-row">
          <motion.div
            :initial="{ opacity: 0, y: 50 }"
            :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
            class="relative w-full overflow-hidden rounded-2xl drop-shadow-lg md:w-7/12"
          >
            <div
              v-if="!isPlaying"
              class="relative cursor-pointer"
              @click="isPlaying = true"
            >
              <img
                :src="story?.image"
                :alt="story?.title"
                class="w-full object-cover"
              >
              <div class="absolute inset-0 z-10 grid place-content-center">
                <div class="relative grid size-16 place-content-center before:absolute before:-inset-2 before:rounded-[50%] before:bg-gray-800/30 before:backdrop-blur-sm after:absolute after:inset-0 after:rounded-[50%] after:bg-gray-800/70 after:backdrop-blur-sm">
                  <UiIcon
                    name="play"
                    class="z-[1] text-2xl text-white"
                  />
                </div>
              </div>
              <div class="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/60 to-transparent px-4 py-3">
                <p class="text-md font-semibold text-white">
                  {{ story?.author?.split(',')[0] }}
                </p>
                <p class="text-md text-white/80">
                  {{ story?.author?.split(',').slice(1).join(',').trim() }}
                </p>
              </div>
            </div>
            <video
              v-else
              autoplay
              controls
              class="w-full"
              :src="story?.videoUrl"
            />
          </motion.div>

          <div class="flex w-full flex-col justify-between rounded-2xl border-4 border-[#FF711833] bg-white px-4 py-6 md:w-5/12">
            <motion.p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="relative w-full text-xl leading-relaxed text-slate-700"
            >
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pb-1"
              >
                <path
                  d="M33.7768 1.80041e-06C34.496 1.86329e-06 35 0.546003 35 1.1585C35 1.59425 34.7463 2.0615 34.1303 2.38875C31.0013 4.046 27.3788 9.78425 27.3788 13.3228C32.6568 13.1618 34.9493 17.8413 34.9493 20.7743C34.9493 24.2848 31.9077 28 27.4102 28C21.7385 28 18.9577 23.5427 18.9577 18.7127C18.9578 8.246 30.9173 1.55042e-06 33.7768 1.80041e-06ZM14.819 1.43069e-07C15.5365 2.05795e-07 16.0423 0.546002 16.0423 1.1585C16.0423 1.59425 15.7885 2.0615 15.1725 2.38875C12.0435 4.046 8.421 9.78425 8.421 13.3227C13.6972 13.1617 15.9915 17.8412 15.9915 20.7742C15.9915 24.2847 12.9482 28 8.4525 28C2.78075 28 -3.42503e-06 23.5427 -3.00278e-06 18.7127C-2.08775e-06 8.246 11.9578 -1.07069e-07 14.819 1.43069e-07Z"
                  fill="#FF7118"
                />
              </svg>
              <span class="">
                {{ story?.pullQuote }}</span>
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pt-1"
              >
                <path
                  d="M1.22325 28C0.504 28 0 27.454 0 26.8415C0 26.4057 0.25375 25.9385 0.86975 25.6112C3.99875 23.954 7.62125 18.2157 7.62125 14.6772C2.34325 14.8382 0.0507501 10.1587 0.0507501 7.22575C0.0507501 3.71525 3.09225 0 7.58975 0C13.2615 0 16.0423 4.45725 16.0423 9.28725C16.0423 19.754 4.08275 28 1.22325 28ZM20.181 28C19.4635 28 18.9578 27.454 18.9578 26.8415C18.9578 26.4057 19.2115 25.9385 19.8275 25.6112C22.9565 23.954 26.579 18.2157 26.579 14.6772C21.3028 14.8382 19.0085 10.1587 19.0085 7.22575C19.0085 3.71525 22.0518 0 26.5475 0C32.2193 0 35 4.45725 35 9.28725C35 19.754 23.0423 28 20.181 28Z"
                  fill="#FF7118"
                />
              </svg>
            </motion.p>
            <motion.p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="pt-4 text-left  text-slate-700"
            >
              <span class="text-lg font-bold text-orange-500 md:text-xl">{{ story?.author?.split(',')[0] }}</span> <br> {{ story?.author?.split(',').slice(1).join(',').trim() }}
            </motion.p>
          </div>
        </div>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          The Challenge
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          At Toco Grill, a kosher Mediterranean restaurant in Atlanta, owner Stephen Newmark manages nearly every part of the business himself. Like many independent restaurant owners, his day moves between the kitchen, the counter, and the dining room—leaving little time for marketing.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          “I knew marketing was important,” he says. “But when you’re running the whole restaurant, it’s hard to stay consistent,” he explains.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          Without a system in place, marketing became reactive and inconsistent. And when restaurant marketing isn’t consistent, visibility drops, and potential customers choose competitors who are easier to find online.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Changed
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          SpotHopper helped Toco Grill replace manual, inconsistent marketing with a connected restaurant marketing system designed to attract new guests and save time.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          The first step was a complete website redesign. SpotHopper built a high-performing restaurant website with professional photography and video, allowing guests to see the food, space, and experience immediately. Key information—menu, hours, and location—became clear and easy to access, improving both user experience and restaurant SEO.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          “SpotHopper was able to help me develop a stellar website to match the diverse menu that I offer,” Stephen says.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          SpotHopper also simplified social media marketing for the restaurant. Instead of creating posts manually, Stephen now reviews and approves pre-built content each month, including promotions, menu features, and updates for Instagram and Facebook.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          This shift is critical for independent restaurant owners. Instead of managing multiple tools and tasks, marketing runs consistently in the background—helping the restaurant stay visible online without requiring constant attention.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How SpotHopper Helped
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          After implementing SpotHopper, Toco Grill saw measurable improvements in restaurant marketing performance:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>630% increase in website traffic

            </span>
          </li>
          <li>
            <span>
              826% increase in organic search traffic (SEO)

            </span>
          </li>
          <li>
            <span>
              166% increase in social media traffic

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          These results show a clear pattern: improved online visibility leads to more diners discovering and choosing the restaurant.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          For Stephen, however, the most valuable outcome wasn’t just growth—it was time.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          SpotHopper saves him approximately two hours every day by automating key marketing tasks. Instead of managing posts, updating content, and trying to stay consistent, he can focus on running the restaurant.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          That time goes back into what matters most: being present with guests, creating a great dining experience, and building relationships that keep people coming back.
        </motion.p>
      </section>
      <div class="mb-8 w-full bg-white pb-8 pt-8 md:rounded-[5rem]">
        <section class="mx-auto mb-8 px-2 lg:max-w-screen-xl lg:pt-16  2xl:px-0">
          <section
            v-if="lastThreeStories.length"
            class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0"
          >
            <motion.h2
              :variants="headlineVariants"
              initial="initial"
              while-in-view="animate"
              class="text-left text-2xl font-medium text-gray-900 lg:text-3xl xl:px-0"
            >
              More Customer Retention Stories
            </motion.h2>
            <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 py-6 md:grid-cols-3">
              <motion.div
                v-for="(item, index) in lastThreeStories"
                :key="item.slug"
                :custom="index"
                :variants="storyVariants"
                initial="initial"
                while-in-view="animate"
              >
                <StoryCard v-bind="item" />
              </motion.div>
            </div>
          </section>
          <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
            <div
              class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20"
            >
              <div
                class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70"
              >
                <motion.img
                  :initial="{ opacity: 0, y: 50 }"
                  :while-in-view="{
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: easeOut },
                  }"
                  :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                  class="aspect-video size-full object-cover object-center"
                  role="presentation"
                />
              </div>

              <div class="isolate grid place-items-center gap-5">
                <motion.h3
                  :variants="headlineVariants"
                  initial="initial"
                  while-in-view="animate"
                  class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
                >
                  Ready to take your restaurant marketing to the next level?
                </motion.h3>

                <UiButton
                  class="group w-full max-w-xs lg:max-w-max"
                  as-child
                >
                  <RouterLink :to="{ name: '/free-demo' }">
                    Book a Free Demo

                    <UiIcon
                      name="arrow-forward-rounded"
                      class="transition-transform duration-200 group-hover:translate-x-1"
                    />
                  </RouterLink>
                </UiButton>
              </div>
            </div>
          </section>
        </section>
      </div>
      <LayoutFooter />
    </div>
  </div>
</template>
